import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { CustomUserDropdownItem } from '@galaxy/atlas';
import { TranslateService } from '@ngx-translate/core';
import { DropdownItem } from '@vendasta/atlas';
import { Observable, combineLatest } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';
import { BusinessNavConfigService } from '../config.service';
import { SidebarService } from '../core-components/sidebar/sidebar.service';
import { BusinessNavDataService } from '../data.service';
import { Capacitor } from '@capacitor/core';

@Component({
  selector: 'atlas-business-navbar',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
})
export class NavigationComponent implements OnInit, OnChanges {
  @Input() staticMenu: string | boolean;
  @Input() partnerId: string;
  @Input() accountId: string;
  @Input() marketId: string;
  @Input() brandPath: string;
  @Input() serviceProviderId: string;
  @Input() hideCenters: string | boolean;
  @Input() hideNotifications: string | boolean;
  @Input() disableUserDropdown: string | boolean;
  @Input() disableNavigation: string | boolean;
  @Input() alwaysShowMenuToggle: string | boolean;
  @Input() showLanguageSelector?: string | boolean = true;
  @Input() defaultURL: string;
  @Output() event: EventEmitter<string> = new EventEmitter<string>();

  public _calculatedStaticMenu: boolean;
  public showMenuHamburger$: Observable<boolean>;
  public dropdownItems$: Observable<CustomUserDropdownItem[]>;

  constructor(
    private sidebarService: SidebarService,
    private cfgService: BusinessNavConfigService,
    private dataService: BusinessNavDataService,
    private translate: TranslateService,
    private matIconRegistry: MatIconRegistry,
    private domSanitzer: DomSanitizer,
  ) {
    this.showMenuHamburger$ = this.sidebarService.showMenuToggle$;
    // Init the nav
    this.calcStaticMenu();
    this.calcDisableNavigation();
    this.calcAlwaysShowMenuToggle();
    this.matIconRegistry.addSvgIconLiteral(
      'store_outlined',
      this.domSanitzer.bypassSecurityTrustHtml(
        `<svg><path d="M18.36 9l.6 3H5.04l.6-3h12.72M20 4H4v2h16V4zm0 3H4l-1 5v2h1v6h10v-6h4v6h2v-6h1v-2l-1-5zM6 18v-4h6v4H6z"/></svg>`,
      ),
    );
  }

  ngOnInit(): void {
    this.refreshConfig();
    this.dropdownItems$ = combineLatest([
      this.dataService.dropdownItems$,
      this.translate.onTranslationChange.pipe(startWith(null)),
    ]).pipe(
      map(([dropdownItems]) =>
        dropdownItems.map((item: DropdownItem) => {
          let label: string = this.translate.instant(item.translationId || item.label);

          if (item.translationId == 'NAVIGATION.MENU.EDIT_PROFILE') {
            label = item.label || 'Edit Profile';
          }

          const i = {
            label: label,
          } as CustomUserDropdownItem;

          // Certain routes in VBC do not use the full URL
          const routesUsingFullURL =
            item.label == 'Sign Out' ||
            item.label == 'Stop Impersonating' ||
            item.translationId == 'NAVIGATION.MENU.EDIT_PROFILE';
          if (this.serviceProviderId.toUpperCase() === 'VBC' && !routesUsingFullURL) {
            i.route = item.path;
          } else {
            i.url = item.url;
          }

          // If we are on a native platform, we need to use the oauth/logout route to logout
          if (item.label == 'Sign Out' && Capacitor.isNativePlatform()) {
            i.url = '/oauth/logout';
          }

          return i;
        }),
      ),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    combineLatest([this.cfgService.serviceProviderId$, this.dataService.disableBusinessNav$]).subscribe(
      ([serviceProviderId, disableBusNav]: [string, boolean]) => {
        this.sidebarService.setDisabledOverride(serviceProviderId !== 'VBC' && disableBusNav);
      },
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.staticMenu) {
      this.calcStaticMenu();
    }
    if (changes.partnerId || changes.marketId || changes.accountId || changes.serviceProviderId || changes.brandPath) {
      this.refreshConfig();
    }
    if (changes.disableNavigation) {
      this.calcDisableNavigation();
    }
    if (changes.alwaysShowMenuToggle) {
      this.calcAlwaysShowMenuToggle();
    }
  }

  toggleMenu(): void {
    this.sidebarService.toggleOpen();
  }

  calcStaticMenu(): void {
    this._calculatedStaticMenu = this.staticMenu === 'true' || this.staticMenu === true;
    if (this._calculatedStaticMenu) {
      this.sidebarService.setOpen(false);
    }
    this.sidebarService.setStatic(this._calculatedStaticMenu);
  }

  private refreshConfig(): void {
    this.cfgService.initialize(
      this.partnerId,
      this.marketId,
      this.accountId,
      this.brandPath,
      this.serviceProviderId,
      this.defaultURL,
    );
  }

  private calcDisableNavigation(): void {
    this.sidebarService.setDisabled(this.disableNavigation === 'true' || this.disableNavigation === true);
  }

  private calcAlwaysShowMenuToggle(): void {
    this.sidebarService.setShowMenuToggle(this.alwaysShowMenuToggle === 'true' || this.alwaysShowMenuToggle === true);
  }

  propagateEvent(eventId: string): void {
    this.event.emit(eventId);
  }
}
